import { Button, Divider, Menu, PopoverPosition, Stack, Switch, Typography } from "@mui/material";
import { Column, Table } from "@tanstack/react-table";
import { useTableSettings } from "src/components";

interface Props<T> {
  tableId: string;
  table: Table<T>;
  anchorPosition: PopoverPosition | undefined;
  onClose: () => void;
}

const TableColumnsMenu = <T extends object>(props: Props<T>) => {
  const { settings, updateSettings } = useTableSettings(props.tableId);

  function handleVisibilityChanged(event: React.ChangeEvent<HTMLInputElement>, column: Column<T, unknown>) {
    column.getToggleVisibilityHandler()(event);

    updateSettings({
      columnVisibility: {
        ...settings.columnVisibility,
        [column.id]: event.target.checked,
      },
    });
  }

  function handleShowAll() {
    const columnVisibilitySettings: Record<string, boolean> = {};

    props.table
      .getAllLeafColumns()
      .filter((column) => column.getCanHide())
      .forEach((column) => {
        column.toggleVisibility(true);
        columnVisibilitySettings[column.id] = true;
      });

    updateSettings({
      columnVisibility: columnVisibilitySettings,
    });
  }

  function handleHideAll() {
    const columnVisibilitySettings: Record<string, boolean> = {};

    props.table
      .getAllLeafColumns()
      .filter((column) => column.getCanHide())
      .forEach((column) => {
        column.toggleVisibility(false);
        columnVisibilitySettings[column.id] = false;
      });

    updateSettings({
      columnVisibility: columnVisibilitySettings,
    });
  }

  return (
    <Menu
      open={Boolean(props.anchorPosition)}
      onClose={props.onClose}
      anchorReference="anchorPosition"
      anchorPosition={props.anchorPosition}
      transformOrigin={{
        horizontal: 30,
        vertical: 30,
      }}
    >
      <Stack spacing={0.5}>
        <Stack spacing={0.25}>
          {props.table
            .getAllLeafColumns()
            .filter((column) => column.getCanHide())
            .map((column) => (
              <Stack direction="row" alignItems="center" spacing={1} key={column.id}>
                <Switch onChange={(e) => handleVisibilityChanged(e, column)} checked={column.getIsVisible()} />
                <Typography>{column.id}</Typography>
              </Stack>
            ))}
        </Stack>
        <Divider variant="dashed" />
        <Stack direction="row" spacing={0.5}>
          <Button variant="tertiary" onClick={handleHideAll}>
            Hide All
          </Button>
          <Button variant="tertiary" onClick={handleShowAll}>
            Show All
          </Button>
        </Stack>
      </Stack>
    </Menu>
  );
};

export { TableColumnsMenu };
